import React, { useState } from "react";
import { staffRepo, roleRepo, branchRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, DataTable, Input, Modal, PageHead, PersonCell, PhoneInput, Select, Switch, useAsync, useTableState } from "../../components/ui";

const EMPTY = { name: "", position: "", phone: "", email: "", branchId: "", roleId: "" };

export default function Staff() {
  const { orgId, toast, checkQuota } = useApp();
  const { tx } = useI18n();
  const tableState = useTableState();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data, loading, reload } = useAsync(
    () => staffRepo.list(orgId, { page: tableState.page, perPage: tableState.perPage, search: tableState.search }),
    [orgId, tableState.page, tableState.perPage, tableState.search]
  );
  const { data: branches } = useAsync(() => branchRepo.all(orgId), [orgId], []);
  const { data: roles } = useAsync(() => roleRepo.forOrg(orgId), [orgId], []);

  const toggleActive = async (row) => {
    await staffRepo.update(orgId, row.id, { isActive: !row.isActive });
    reload();
  };

  const save = async () => {
    if (!form.name || !form.position) return toast(tx("กรอกชื่อและตำแหน่งให้ครบ", "Name and position required"), "error");

    // โควตาพนักงานตามแพ็กเกจ
    const quota = checkQuota("staff", data?.total || 0);
    if (!quota.ok) {
      return toast(
        tx(`แพ็กเกจนี้เพิ่มพนักงานได้ ${quota.limit} คน อัปเกรดแพ็กเกจหรือซื้อพนักงานเพิ่มที่หน้าฟีเจอร์เสริม`, `Your plan allows ${quota.limit} staff — upgrade or buy extra quota under Add-ons`),
        "error"
      );
    }

    await staffRepo.create(orgId, { ...form, branchId: form.branchId || branches?.[0]?.id, commissionRate: 0, isActive: true, avatar: `${Math.floor(Math.random() * 30) + 1}.png` });
    setOpen(false);
    setForm(EMPTY);
    reload();
    toast(tx("เพิ่มพนักงานเรียบร้อย", "Staff added"), "success");
  };

  return (
    <>
      <PageHead
        title={tx("จัดการพนักงาน", "Staff management")}
        subtitle={tx("รายชื่อพนักงาน แพทย์ เซลส์ ช่าง และสิทธิ์การใช้งาน", "Staff, specialists, sales and permissions")}
        actions={<Button variant="primary" onClick={() => setOpen(true)}>+ {tx("เพิ่มพนักงาน", "Add staff")}</Button>}
      />

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        emptyText={tx("ยังไม่มีพนักงาน", "No staff")}
        columns={[
          { key: "name", label: tx("พนักงาน", "Staff"), render: (row) => <PersonCell name={row.name} sub={row.position} /> },
          { key: "phone", label: tx("เบอร์โทร", "Phone") },
          { key: "email", label: tx("อีเมล", "Email") },
          { key: "commissionRate", label: tx("อัตราคอมมิชชัน", "Commission"), align: "right", render: (row) => `${row.commissionRate}%` },
          { key: "isActive", label: tx("ใช้งาน", "Active"), render: (row) => <Switch checked={row.isActive} onChange={() => toggleActive(row)} /> },
        ]}
      />

      <Modal
        open={open}
        title={tx("เพิ่มพนักงานใหม่", "New staff")}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{tx("ยกเลิก", "Cancel")}</Button>
            <Button variant="primary" onClick={save}>{tx("บันทึก", "Save")}</Button>
          </>
        }
      >
        <Input label={tx("ชื่อ-นามสกุล", "Full name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input label={tx("ตำแหน่ง", "Position")} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
        <PhoneInput label={tx("เบอร์โทร", "Phone")} value={form.phone} onChange={(value) => setForm({ ...form, phone: value })} />
        <Input label={tx("อีเมล", "Email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Select label={tx("สาขา", "Branch")} placeholder={tx("เลือกสาขา", "Select branch")} options={(branches || []).map((b) => ({ value: b.id, label: b.name }))} value={form.branchId} onChange={(e) => setForm({ ...form, branchId: e.target.value })} />
        <Select label={tx("บทบาท", "Role")} placeholder={tx("เลือกบทบาท", "Select role")} options={(roles || []).map((r) => ({ value: r.id, label: r.name }))} value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })} />
      </Modal>
    </>
  );
}
